import { useEffect, useState } from "react";
import { toast } from "sonner";
import { QrCode, Copy, DownloadSimple, ArrowsClockwise, Prohibit } from "@phosphor-icons/react";
import api, { apiErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const BASE_KEY = "strata:portal-base-url";

export function QrCodeModal({ open, onOpenChange, properties, defaultPropertyId, onChanged }) {
  const [pid, setPid] = useState("");
  const [link, setLink] = useState(null);
  const [base, setBase] = useState(() => localStorage.getItem(BASE_KEY) || window.location.origin);
  const [expires, setExpires] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const prop = properties.find((p) => p.id === pid);

  useEffect(() => { if (open) setPid(defaultPropertyId || properties[0]?.id || ""); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!open || !pid) { setLink(null); return; }
    api.get("/service/portal-links", { params: { property_id: pid } }).then(({ data }) => { setLink(data[0] || null); setExpires(data[0]?.expires_at || ""); }).catch(() => setLink(null));
  }, [open, pid]);

  const run = async (fn) => { setBusy(true); try { return await fn(); } catch (e) { toast.error(apiErrorMessage(e, "Action failed")); return null; } finally { setBusy(false); } };
  const downloadPdf = async (l) => {
    const r = await api.get(`/service/portal-links/${l.id}/pdf`, { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url; a.download = `Service_Logs_QR_${(prop?.street || "property").replace(/[^a-z0-9]+/gi, "_")}.pdf`; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  };
  const generate = () => run(async () => {
    localStorage.setItem(BASE_KEY, base.replace(/\/$/, ""));
    const { data } = await api.post("/service/portal-links", { property_id: pid, base_url: base.replace(/\/$/, ""), expires_at: expires || null });
    setLink(data); onChanged?.();
    await downloadPdf(data);
    toast.success(data.existing ? "Downloaded the existing QR sheet (same link)" : "QR link created — PDF downloaded");
  });
  const saveSettings = () => run(async () => {
    const { data } = await api.patch(`/service/portal-links/${link.id}`, { base_url: base.replace(/\/$/, ""), expires_at: expires || null });
    setLink(data); toast.success("Link settings saved");
  });
  const doConfirm = () => run(async () => {
    if (confirm === "revoke") { await api.post(`/service/portal-links/${link.id}/revoke`); setLink(null); toast.success("QR link revoked"); }
    else { const { data } = await api.post(`/service/portal-links/${link.id}/regenerate`); setLink(data); await downloadPdf(data); toast.success("New QR link issued — old QR codes no longer work"); }
    setConfirm(null); onChanged?.();
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby={undefined} className="max-w-lg" data-testid="qr-modal">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><QrCode size={20} /> Generate QR Code</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Property</Label>
            <Select value={pid} onValueChange={setPid}>
              <SelectTrigger data-testid="qr-property-select"><SelectValue placeholder="Choose property" /></SelectTrigger>
              <SelectContent>{properties.map((p) => <SelectItem key={p.id} value={p.id}>{p.street}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {prop && <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-semibold text-slate-800" data-testid="qr-address-preview">{prop.full_address}</div>}
          <div className="grid grid-cols-5 gap-2">
            <div className="col-span-3"><Label className="text-xs">Portal web address</Label><Input value={base} onChange={(e) => setBase(e.target.value)} placeholder="https://service.yourdomain.com" data-testid="qr-base-url-input" /></div>
            <div className="col-span-2"><Label className="text-xs">Expires (optional)</Label><Input type="date" value={expires || ""} onChange={(e) => setExpires(e.target.value)} data-testid="qr-expires-input" /></div>
          </div>
          {link ? (
            <div className="rounded-md border border-emerald-200 bg-emerald-50/60 p-3 space-y-2" data-testid="qr-active-link">
              <div className="text-xs font-bold uppercase tracking-wider text-emerald-700">Active QR link{link.expires_at ? ` · expires ${link.expires_at}` : " · never expires"}</div>
              <div className="text-[11px] text-slate-600 break-all font-mono" data-testid="qr-link-url">{link.url}</div>
              <div className="flex flex-wrap gap-2">
                <Button size="sm" onClick={() => run(() => downloadPdf(link))} disabled={busy} className="bg-[#327de3] hover:bg-[#1f64c2] text-white" data-testid="qr-download-again-btn"><DownloadSimple size={13} className="mr-1" /> Download Again</Button>
                <Button size="sm" variant="outline" onClick={() => { navigator.clipboard?.writeText(link.url); toast.success("Portal link copied"); }} data-testid="qr-copy-link-btn"><Copy size={13} className="mr-1" /> Copy Portal Link</Button>
                <Button size="sm" variant="outline" onClick={saveSettings} disabled={busy} data-testid="qr-save-settings-btn">Save settings</Button>
                <Button size="sm" variant="outline" onClick={() => setConfirm("regenerate")} className="text-amber-700 border-amber-200" data-testid="qr-regenerate-btn"><ArrowsClockwise size={13} className="mr-1" /> Regenerate</Button>
                <Button size="sm" variant="outline" onClick={() => setConfirm("revoke")} className="text-rose-600 border-rose-200" data-testid="qr-revoke-btn"><Prohibit size={13} className="mr-1" /> Revoke</Button>
              </div>
            </div>
          ) : (
            <Button onClick={generate} disabled={!pid || busy} className="w-full bg-orange-500 hover:bg-orange-600 text-white" data-testid="qr-generate-btn"><QrCode size={15} className="mr-1.5" /> Generate / Download PDF</Button>
          )}
          <p className="text-[11px] text-slate-500 leading-relaxed">Anyone with this QR code or link can view this property's service logs and add new entries (no editing or deleting) until it is revoked or expires. It is a shareable link, not a login.</p>
        </div>
        <AlertDialog open={!!confirm} onOpenChange={(v) => !v && setConfirm(null)}>
          <AlertDialogContent data-testid="qr-confirm-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>{confirm === "revoke" ? "Revoke this QR link?" : "Regenerate this QR link?"}</AlertDialogTitle>
              <AlertDialogDescription>Printed QR codes for the current link will stop working immediately. Service history is kept.</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="qr-confirm-cancel">Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={doConfirm} className="bg-rose-600 hover:bg-rose-700" data-testid="qr-confirm-ok">{confirm === "revoke" ? "Revoke" : "Regenerate"}</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </DialogContent>
    </Dialog>
  );
}
